/**
 * File: apps/qbench-cp/reactapp/src/shared/components/FormV2/Date.js
 *
 * Purpose: Renders the date/datetime picker input used by FormV2 for DATE and DATETIME field
 *     types.
 *
 * Scope: FormV2 input rendering for date and datetime entity fields.
 *
 * Tags: customer-portal
 *
 * Overview: Exports Date, a memoized component wrapping react-datetime's DateTime, bound to
 *     react-hook-form via useController. It renders a custom input with a clear button
 *     (visible when a value is set and the field is editable) and, when the picker opens,
 *     scrolls the calendar into view and resets its displayed month to today. Whether the time
 *     portion is shown is driven by comparing fieldType against AddlFieldType.DATETIME. The
 *     attached `transform` strategy converts between the server's date representation and a
 *     local date value via fromLocalDate/toLocalDate, varying behavior based on fieldType.
 *
 * Exports: default Date component (with an attached `transform` static property).
 */

import moment from 'moment';
import classNames from 'classnames';
import React, { useRef } from 'react';
import DateTime from 'react-datetime';
import styled from 'styled-components';
import { useController } from 'react-hook-form';
import closeIcon from 'assets/images/svg/close.svg';
import { AddlFieldType } from 'shared/constants/fields';
import { fromLocalDate, toLocalDate } from 'shared/utils/form';
import { noop } from 'shared/utils/misc';
import { readOnlyAttrs } from './Input';
import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_TIME_FORMAT,
} from 'shared/constants/misc';

/**
 * @type {import('./types').FieldTransformStrategy}
 */
const transform = {
  fromServer(value, { field }) {
    const { fieldType } = field;
    const isDateTime = fieldType === AddlFieldType.DATETIME;
    return toLocalDate(value, isDateTime);
  },
  toServer(value, { field }) {
    const { fieldType } = field;
    const isDateTime = fieldType === AddlFieldType.DATETIME;
    return fromLocalDate(value, isDateTime);
  },
  defaultValue(value) {
    return value;
  },
};

const Date = React.memo(function Date({
  name,
  control,
  displayName,
  fieldType,
  required,
  readOnly,
}) {
  const dateTimeRef = useRef(null);
  const {
    field: { onChange, onBlur, value, ref },
  } = useController({
    name,
    control,
    rules: { required },
  });
  const isDateTime = fieldType === AddlFieldType.DATETIME;
  const inputProps = {
    id: name,
    readOnly,
    autoComplete: 'off',
    placeholder: displayName,
    className: classNames(
      'w-100 h-input p-2 mb-4 fs-7 fw-normal lh-sm border-0 border-bottom border-input',
      { 'bg-light text-muted cursor-not-allowed': readOnly }
    ),
    ...(readOnly && readOnlyAttrs),
  };

  function setTodayAndScrollIntoView() {
    if (!dateTimeRef.current) return;

    dateTimeRef.current.setViewDate(moment());
    setTimeout(() => {
      const picker = document.querySelector('.rdtPicker');
      if (picker) {
        picker.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });
      }
    });
  }

  function renderInputWithClearBtn(props) {
    const { value, onChange } = props;
    const clear = () => onChange({ target: { value: '' } });
    return (
      <div className="position-relative">
        <input readOnly {...props} />
        {value && !readOnly && (
          <ClearButton
            className="cursor-pointer position-absolute bg-transparent"
            onClick={clear}
          >
            <img src={closeIcon} alt="close" />
          </ClearButton>
        )}
      </div>
    );
  }

  return (
    <DateTime
      strictParsing
      ref={dateTimeRef}
      value={value}
      inputRef={ref}
      onClose={onBlur}
      onChange={readOnly ? noop : onChange}
      onOpen={readOnly ? noop : setTodayAndScrollIntoView}
      inputProps={inputProps}
      renderInput={renderInputWithClearBtn}
      timeFormat={isDateTime && DEFAULT_TIME_FORMAT}
      dateFormat={DEFAULT_DATE_FORMAT}
      closeOnSelect
    />
  );
});

const ClearButton = styled.button`
  right: 0.625rem;
  top: 0.4375rem;
  img {
    height: 1.25rem;
  }
`;

Date.transform = transform;
export default Date;
